import find from "lodash/find";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Button } from "PFComponents/button";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useInvites } from "PFCore/hooks/queries/invites/use_invites";
import { useSendInvitation } from "PFCore/hooks/queries/invites/use_send_invitation";
import { Activity, Profile } from "PFTypes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type DisabledButtonProps = { text: string };

export const DisabledButton = ({ text }: DisabledButtonProps) => (
  <Button text={text} kind="secondary" disabled style={{ minWidth: 80 }} />
);

type InviteButtonProps = {
  profile: Pick<Profile, "id">;
  task: Activity;
};

export const InviteButton = ({ profile, task }: InviteButtonProps) => {
  const { t } = useTranslation("activities");
  const { showGrowl } = useGrowlStoreActions();
  const { getSingleErrorByType } = useResponseErrors();

  const { data: invites } = useInvites(task.id);
  const { mutate: sendInvitation, isPending } = useSendInvitation([task.id], [profile.id]);

  const invitation = find(invites, { profileId: profile.id });
  const isInvited = !!invitation;
  const isParticipant = invitation && invitation.state === "accepted";
  const [invitationSubmitted, markInvitationSubmit] = useState(false);

  const handleInvitationSuccess = () => {
    markInvitationSubmit(true);

    showGrowl({
      message: t("show.parts.taskInviteSuccess")
    });
  };

  const handleInvitationFailed = ({ response }) => {
    const formattedErrors = getSingleErrorByType(response);
    if (formattedErrors.profileId[0] === "has already been taken") {
      markInvitationSubmit(true);
      return;
    }

    showGrowl({
      kind: "error",
      message: t("show.parts.taskInviteError")
    });
  };

  if (!invites) {
    return null;
  }

  if (isParticipant) {
    return <DisabledButton text={t("show.parts.responded")} />;
  }

  if (isInvited || invitationSubmitted) {
    return <DisabledButton text={t("show.matches.invited")} />;
  }

  if (isPending) {
    return <DisabledButton text="..." />;
  }

  return (
    <Button
      text={t("show.parts.invite")}
      style={{ minWidth: 80, marginRight: 5 }}
      kind="secondary"
      qaId="shortlist-invite"
      onClick={() =>
        sendInvitation(null, {
          onSuccess: handleInvitationSuccess,
          onError: handleInvitationFailed
        })
      }
    />
  );
};
